import { useEffect, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { Search, Filter, RefreshCw } from 'lucide-react';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { StockTable } from '../components/dashboard/StockTable';
import { useMarketStore, useAuthStore } from '../store';
import { runMarketScanner, getSectorBreakdown } from '../utils/api';
import type { MarketScanner } from '../types';

// Tier level mapping
const tierLevel: Record<string, number> = {
  'free': 0,
  'starter': 1,
  'early_supporter': 1,
  'pro': 2,
  'enterprise': 3
};

// Tier-based result limits
const tierLimits: Record<number, number[]> = {
  0: [5],                    // Free: only 5
  1: [10, 25],               // Starter/Early Supporter: up to 25
  2: [10, 25, 50, 100],      // Pro: up to 100
  3: [10, 25, 50, 100, 250], // Enterprise: up to 250
};

// Cache expiry time (5 minutes)
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

const SECTORS = [
  'Technology',
  'Healthcare',
  'Financial Services',
  'Consumer Cyclical',
  'Communication Services',
  'Industrials',
  'Consumer Defensive',
  'Energy',
  'Basic Materials',
  'Real Estate',
  'Utilities',
];

export function Scanner() {
  const { scannerData, setScannerData, lastScanTime, setLastScanTime } = useMarketStore();
  const { user } = useAuthStore();

  const userTier = user?.subscription_tier || 'free';
  const userTierLevel = tierLevel[userTier] || 0;
  const isFreeUser = userTierLevel === 0;

  const [loading, setLoading] = useState(!scannerData);
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [tickerFilter, setTickerFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sectorBreakdown, setSectorBreakdown] = useState<Record<string, number>>({});

  // Get available limit options based on tier
  const availableLimits = tierLimits[userTierLevel] || [5];
  const maxTierLimit = availableLimits[availableLimits.length - 1];
  const [resultLimit, setResultLimit] = useState<number>(maxTierLimit);

  const fetchSectors = async () => {
    try {
      const data = await getSectorBreakdown();
      setSectorBreakdown(data);
    } catch (error) {
      console.error('Failed to fetch sector breakdown:', error);
    }
  };

  const fetchData = useCallback(async (force = false) => {
    // Check if cache is still valid (unless forced refresh)
    if (!force && scannerData && lastScanTime) {
      const cacheAge = Date.now() - new Date(lastScanTime).getTime();
      if (cacheAge < CACHE_EXPIRY_MS) {
        return; // Use cached data
      }
    }

    try {
      setLoading(true);

      const tickers = tickerFilter
        .split(',')
        .map((t) => t.trim().toUpperCase())
        .filter((t) => t);

      const data: MarketScanner = await runMarketScanner(
        tickers.length > 0 ? tickers : undefined,
        selectedSectors.length > 0 ? selectedSectors : undefined,
        maxTierLimit
      );

      setScannerData(data);
      setLastScanTime(new Date().toISOString());
    } catch (error) {
      console.error('Scan failed:', error);
    } finally {
      setLoading(false);
    }
  }, [tickerFilter, selectedSectors, maxTierLimit, scannerData, lastScanTime, setScannerData, setLastScanTime]);

  // Apply filters - force refresh with current filter settings
  const applyFilters = () => {
    fetchData(true);
  };

  useEffect(() => {
    fetchSectors();
    fetchData();
  }, []);

  // Limit stocks based on tier and selected limit
  const buyOpportunities = (scannerData?.buy_opportunities || []).slice(0, resultLimit);
  const sellSignals = (scannerData?.sell_signals || []).slice(0, resultLimit);

  const toggleSector = (sector: string) => {
    setSelectedSectors((prev) =>
      prev.includes(sector) ? prev.filter((s) => s !== sector) : [...prev, sector]
    );
  };

  const clearFilters = () => {
    setSelectedSectors([]);
    setTickerFilter('');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">
            Market Scanner
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Find contrarian opportunities: stocks with LOW sentiment but STRONG fundamentals (BUY), or HIGH sentiment and stretched valuations (SELL)
          </p>
        </div>

        <Button
          variant="secondary"
          onClick={() => setShowFilters(!showFilters)}
          icon={<Filter className="h-4 w-4" />}
        >
          Filters
          {(selectedSectors.length > 0 || tickerFilter) && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">
              {selectedSectors.length + (tickerFilter ? 1 : 0)}
            </span>
          )}
        </Button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <div className="space-y-4">
            {/* Ticker Input */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Filter by Tickers (comma-separated)
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-muted)]" />
                <input
                  type="text"
                  value={tickerFilter}
                  onChange={(e) => setTickerFilter(e.target.value.toUpperCase())}
                  placeholder="AAPL, TSLA, MSFT..."
                  className={clsx(
                    'w-full h-10 pl-10 pr-4 rounded-lg',
                    'bg-[var(--color-bg-secondary)] border border-[var(--color-border)]',
                    'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500/50'
                  )}
                />
              </div>
            </div>

            {/* Sector Filters */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Filter by Sector
              </label>
              <div className="flex flex-wrap gap-2">
                {SECTORS.map((sector) => (
                  <button
                    key={sector}
                    onClick={() => toggleSector(sector)}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer',
                      selectedSectors.includes(sector)
                        ? 'bg-blue-500 text-white'
                        : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)]'
                    )}
                  >
                    {sector}
                    {sectorBreakdown[sector] && (
                      <span className="ml-1 text-xs opacity-70">
                        ({sectorBreakdown[sector]})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Results Limit */}
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                Results Per Section
              </label>
              <div className="flex items-center gap-2">
                {availableLimits.map((limit) => (
                  <button
                    key={limit}
                    onClick={() => setResultLimit(limit)}
                    className={clsx(
                      'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                      resultLimit === limit
                        ? 'bg-blue-500 text-white'
                        : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)] hover:bg-[var(--color-border)]'
                    )}
                  >
                    Top {limit}
                  </button>
                ))}
                {isFreeUser && (
                  <span className="text-xs text-[var(--color-text-muted)] ml-2">
                    Upgrade for more results
                  </span>
                )}
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end gap-2">
              {(selectedSectors.length > 0 || tickerFilter) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Clear All Filters
                </Button>
              )}
              <Button size="sm" onClick={applyFilters} icon={<RefreshCw className="h-4 w-4" />}>
                Apply Filters
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Results Tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <StockTable
          title={`Top ${resultLimit} Buy Opportunities`}
          type="buy"
          stocks={buyOpportunities}
          loading={loading}
        />
        <StockTable
          title={`Top ${resultLimit} Sell Signals`}
          type="sell"
          stocks={sellSignals}
          loading={loading}
        />
      </div>

      {/* Free tier upgrade prompt */}
      {isFreeUser && scannerData && (
        <Card className="text-center py-6">
          <p className="text-[var(--color-text-muted)] mb-2">
            Free users can view the top 5 buy opportunities (low sentiment + strong fundamentals) and top 5 sell signals (high sentiment = stretched).
          </p>
          <p className="text-sm text-[var(--color-text-muted)]">
            Upgrade to Starter for top 25 or Pro for top 100 results, plus full analysis of {scannerData.stocks_analyzed} stocks.
          </p>
        </Card>
      )}
    </div>
  );
}
